import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'eph-theme';

// Couleurs de fond des deux themes : la barre systeme Android suit cette
// valeur, sinon elle reste claire au-dessus d'une app sombre.
const THEME_COLOR: Record<Theme, string> = {
  light: '#fbfbfa',
  dark: '#0c0c0d',
};

/**
 * Preference purement visuelle, donc stockee cote navigateur plutot qu'en
 * base : elle doit s'appliquer avant le premier rendu, sans attendre le
 * reseau. Le script inline de `index.html` la lit au chargement.
 */
export function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Mode navigation privee ou stockage bloque : on retombe sur le systeme.
  }
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', THEME_COLOR[theme]);
}

export function useTheme(): [Theme, (next: Theme) => void] {
  const [theme, setThemeState] = useState<Theme>(() =>
    document.documentElement.dataset.theme === 'light' ? 'light' : 'dark',
  );

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Sans persistance, le theme reste valable pour la session en cours.
    }
  }, []);

  return [theme, setTheme];
}
